import React, { useState, useRef, useEffect } from 'react';
import { api } from '../App';
import { MessageCircle, X, Send } from 'lucide-react';

const sessionId = () => {
  let id = sessionStorage.getItem('sraivest_assistant_session');
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem('sraivest_assistant_session', id);
  }
  return id;
};

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: 'assistant', content: 'Hi! Ask me anything about how Sraivest works.' }]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setText('');
    setBusy(true);
    try {
      const res = await api.post('/assistant/chat', { session_id: sessionId(), message: q });
      setMsgs((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: 'assistant', content: err.response?.data?.detail || 'Sorry, I could not answer right now.' }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="assistant-open-btn" aria-label="Open help assistant"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#00E676] text-black shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[480px] rounded-2xl bg-[#111] border border-white/10 shadow-2xl flex flex-col" data-testid="assistant-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <p className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Sraivest Help</p>
        <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="assistant-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`text-sm px-3 py-2 rounded-xl max-w-[85%] whitespace-pre-wrap ${m.role === 'user' ? 'ml-auto bg-[#00E676] text-black' : 'bg-white/10 text-gray-100'}`}>{m.content}</div>
        ))}
        {busy && <div className="text-xs text-gray-500" data-testid="assistant-typing">Thinking…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={800} placeholder="Ask a question…" data-testid="assistant-input"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E676]" />
        <button type="submit" disabled={busy} data-testid="assistant-send-btn" className="px-3 rounded-lg bg-[#00E676] text-black disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </form>
      <p className="px-3 pb-2 text-[10px] text-gray-500">General help only. Not investment, legal or tax advice.</p>
    </div>
  );
};
